import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import './Legal.css';
import './AccountDeletion.css';

const AccountDeletion = () => {
  const navigate = useNavigate();
  const userString = localStorage.getItem('user');
  const user = userString ? JSON.parse(userString) : null;
  const token = localStorage.getItem('token');
  
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  const handleDeleteAccount = async () => {
    if (confirmText !== 'DELETE') return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete account');
      }

      // Clear local storage
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      
      setIsDeleted(true);
      toast.success('Your account has been permanently deleted.');
      
      // Redirect to home after a brief pause
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
      
    } catch (error) {
      console.error('Account deletion error:', error);
      toast.error(error.message || 'Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Show success state after deletion
  if (isDeleted) {
    return (
      <div className="legal-container">
        <div className="deletion-success">
          <div className="success-icon">&#10003;</div>
          <h2>Account Deleted</h2>
          <p>Your FreshFitLab account and all associated data have been permanently deleted.</p>
          <p>You will be redirected to the home page shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="legal-container">
      <h1>Delete Your Account</h1>
      <p className="last-updated">FreshFitLab Account Deletion</p>

      {/* Public information section - visible to everyone */}
      <section>
        <h2>How to Delete Your FreshFitLab Account</h2>
        <p>
          FreshFitLab allows you to permanently delete your account and all associated data. 
          Follow these steps to request account deletion:
        </p>
        <ol className="deletion-steps">
          <li>
            <strong>Sign in</strong> to your FreshFitLab account using Google Sign-In.
          </li>
          <li>
            <strong>Navigate to this page</strong> ({window.location.origin}/delete-account) while signed in.
          </li>
          <li>
            <strong>Confirm deletion</strong> by typing "DELETE" in the confirmation field and clicking the delete button.
          </li>
          <li>
            Your account and all data will be <strong>permanently and immediately deleted</strong>.
          </li>
        </ol>
      </section>

      <section>
        <h2>Data That Will Be Deleted</h2>
        <p>When you delete your account, the following data is <strong>permanently removed</strong>:</p>
        <ul>
          <li>Your user profile (name, email, profile picture)</li>
          <li>All personal recipes and menus you have created</li>
          <li>Your meal plans and meal plan preferences</li>
          <li>Nutrition goals and plans</li>
          <li>Shopping list history and Instacart purchase tracking</li>
          <li>Dietary preferences and ingredient preferences</li>
          <li>Menu activity and interaction history (likes, views)</li>
          <li>Any active subscriptions (will be cancelled)</li>
          <li>Affiliate account data (if applicable)</li>
          <li>Coach-client relationships (if applicable)</li>
        </ul>
      </section>

      <section>
        <h2>Data Retention</h2>
        <p>
          Account deletion is <strong>immediate and permanent</strong>. There is no retention period 
          and no way to recover your data after deletion. All data listed above is removed from our 
          servers at the time of deletion.
        </p>
        <p>
          <strong>Note:</strong> If you have an active paid subscription, it will be cancelled 
          immediately upon account deletion. No refund will be issued for the remaining billing period.
        </p>
      </section>

      <section>
        <h2>Contact Us</h2>
        <p>
          If you have questions about account deletion or need assistance, please contact us at:
        </p>
        <p>Email: <a href="mailto:support@freshfitlab.io">support@freshfitlab.io</a></p>
      </section>

      {/* Action section - different for logged in vs logged out */}
      <section className="deletion-action-section">
        {user ? (
          <>
            <h2>Delete Your Account</h2>
            <div className="account-info">
              <p>You are signed in as:</p>
              <div className="user-card">
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="user-avatar-small" />
                )}
                <div>
                  <strong>{user.name}</strong>
                  <br />
                  <span className="user-email">{user.email}</span>
                </div>
              </div>
            </div>

            {!showConfirmation ? (
              <button 
                className="delete-button-initial"
                onClick={() => setShowConfirmation(true)}
              >
                I want to delete my account
              </button>
            ) : (
              <div className="confirmation-box">
                <div className="warning-banner">
                  <span className="warning-icon">&#9888;</span>
                  <div>
                    <strong>Warning: This action cannot be undone!</strong>
                    <p>
                      All your data including recipes, meal plans, preferences, and account 
                      information will be permanently deleted. This cannot be reversed.
                    </p>
                  </div>
                </div>
                
                <p className="confirm-instruction">
                  Type <strong>DELETE</strong> below to confirm:
                </p>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  className="confirm-input"
                  disabled={isDeleting}
                />
                
                <div className="confirmation-buttons">
                  <button 
                    className="cancel-button"
                    onClick={() => {
                      setShowConfirmation(false);
                      setConfirmText('');
                    }}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button 
                    className="delete-button-final"
                    onClick={handleDeleteAccount}
                    disabled={confirmText !== 'DELETE' || isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Permanently Delete My Account'}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="login-prompt">
            <h2>Sign In Required</h2>
            <p>
              You must be signed in to your FreshFitLab account to delete it. 
              Please sign in using the button in the navigation bar above, then return to this page.
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

export default AccountDeletion;
